import React, { useState, useEffect, useRef } from 'react';
import './AddUser.css';
import { api } from '../config/api';

interface UserResponse {
  userId: string;
  displayUserId: string;
  name: string;
  email: string;
  phoneNo?: string;
  role?: string;
  isVerified?: boolean;
  isBanned?: boolean;
  isDeleted?: boolean;
}

interface User {
  userId: string;
  displayUserId: string;
  name: string;
  email: string;
  phoneNo: string;
  role: string;
  isVerified: boolean;
  isBanned: boolean;
  isDeleted: boolean;
}

interface AddUserProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (userId: string) => void;
  currentOwnerId: string;
  type: 'operator' | 'staff' | 'owner';
  currentOperators: User[];
  currentStaff: User[];
  startInCreateMode?: boolean;
}

const AddUser: React.FC<AddUserProps> = ({ isOpen, onClose, onConfirm, currentOwnerId, type, currentOperators, currentStaff, startInCreateMode = false }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isCreatingNew, setIsCreatingNew] = useState(startInCreateMode);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processStep, setProcessStep] = useState<string>('');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [newUser, setNewUser] = useState({
    name: '',
    email: '',
    password: '',
    phoneNo: '',
    role: 'Operator'
  });
  const [emailError, setEmailError] = useState<string | null>(null);

  // Filter users based on search term
  const filteredUsers = users.filter(user => {
    const searchLower = searchTerm.toLowerCase();
    const formattedUserId = user.displayUserId.replace('PWP-U-', '');

    return (
      user.name.toLowerCase().includes(searchLower) ||
      user.email.toLowerCase().includes(searchLower) ||
      formattedUserId.toLowerCase().includes(searchLower) ||
      user.displayUserId.toLowerCase().includes(searchLower)
    );
  });

  // Add keyboard event handlers
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (!isProcessing) {
          if (isDropdownOpen) {
            setIsDropdownOpen(false);
            setHighlightedIndex(-1);
          } else {
            onClose();
          }
        }
      } else if (e.key === "Enter" && !isProcessing) {
        // Only trigger confirm if applicable
        const target = e.target as HTMLElement;
        if (target.tagName !== "INPUT" && target.tagName !== "SELECT" && target.tagName !== "TEXTAREA") {
          if (isCreatingNew) {
            if (newUser.name && newUser.email && newUser.password) {
              handleCreateUser();
            }
          } else if (selectedUserId) {
            onConfirm(selectedUserId);
          }
        }
      } else if (isDropdownOpen && (e.key === "ArrowDown" || e.key === "ArrowUp")) {
        e.preventDefault();
        const currentIndex = highlightedIndex;
        let newIndex = currentIndex;

        if (e.key === "ArrowDown") {
          newIndex = currentIndex < filteredUsers.length - 1 ? currentIndex + 1 : 0;
        } else if (e.key === "ArrowUp") {
          newIndex = currentIndex > 0 ? currentIndex - 1 : filteredUsers.length - 1;
        }

        setHighlightedIndex(newIndex);
      } else if (isDropdownOpen && e.key === "Enter" && highlightedIndex >= 0) {
        e.preventDefault();
        const selectedUser = filteredUsers[highlightedIndex];
        if (selectedUser) {
          handleUserSelect(selectedUser.userId);
        }
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen, isProcessing, isCreatingNew, newUser, selectedUserId, onClose, onConfirm, isDropdownOpen, highlightedIndex, filteredUsers]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
        setHighlightedIndex(-1);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Reset highlighted index when search term changes
  useEffect(() => {
    setHighlightedIndex(-1);
  }, [searchTerm]);

  const generateTempPassword = () => {
    const letters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const numbers = '0123456789';
    let password = '';

    // Generate 3 random uppercase letters
    for (let i = 0; i < 3; i++) {
      password += letters.charAt(Math.floor(Math.random() * letters.length));
    }

    // Generate 3 random numbers
    for (let i = 0; i < 3; i++) {
      password += numbers.charAt(Math.floor(Math.random() * numbers.length));
    }

    // Shuffle the password
    return password.split('').sort(() => Math.random() - 0.5).join('');
  };

  useEffect(() => {
    const fetchUsers = async () => {
      setIsLoading(true);
      try {
        const response = await api.get('/users/get-all-users');
        const data = response.data;
        if (data.success) {
          // Filter users based on type and current users
          let filteredUsers: User[] = data.data;

          if (type === 'owner') {
            filteredUsers = data.data.filter((user: UserResponse) =>
              user.userId !== currentOwnerId
            );
          } else if (type === 'operator') {
            const currentOperatorIds = currentOperators.map(op => op.userId);
            filteredUsers = data.data.filter((user: UserResponse) =>
              !currentOperatorIds.includes(user.userId) && user.userId !== currentOwnerId
            );
          } else if (type === 'staff') {
            const currentStaffIds = currentStaff.map(staff => staff.userId);
            const currentOperatorIds = currentOperators.map(op => op.userId);
            filteredUsers = data.data.filter((user: UserResponse) =>
              !currentStaffIds.includes(user.userId) &&
              !currentOperatorIds.includes(user.userId) &&
              user.userId !== currentOwnerId
            );
          }

          setUsers(filteredUsers);
        } else {
          console.error('Failed to fetch users:', data.message);
          setError('Failed to load users');
        }
      } catch (error) {
        console.error('Error fetching users:', error);
        setError('Failed to load users');
      } finally {
        setIsLoading(false);
      }
    };

    if (isOpen) {
      fetchUsers();
      setSelectedUserId('');
      setSearchTerm('');
      setIsDropdownOpen(false);
      setHighlightedIndex(-1);
      setIsCreatingNew(startInCreateMode);
      setNewUser({
        name: '',
        email: '',
        password: generateTempPassword(),
        phoneNo: '',
        role: 'Operator'
      });
    }
  }, [isOpen, currentOwnerId, type, currentOperators, currentStaff, startInCreateMode]);

  const handleCreateUser = async () => {
    try {
      setIsProcessing(true);
      setError(null);
      setProcessStep('Creating User...');

      // Validate email format
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(newUser.email)) {
        setEmailError('Please enter a valid email address');
        setIsProcessing(false);
        return;
      }

      // Get the current user's ID from localStorage
      const currentUserId = localStorage.getItem('userId');
      if (!currentUserId) {
        throw new Error('User session expired. Please log in again.');
      }

      const response = await api.post('/users/create-user', {
        name: newUser.name,
        email: newUser.email.toLowerCase(),
        tempPassword: newUser.password,
        phoneNo: newUser.phoneNo,
        role: newUser.role,
        createdBy: currentUserId
      });

      const data = response.data;

      if (!data.success) {
        if (data.message === 'USER00002') {
          setEmailError('A user with this email already exists');
          setIsProcessing(false);
          return;
        }
        throw new Error(`Failed to create user: ${data.message}`);
      }

      setProcessStep('User Created! Sending Email...');
      const createdUser = data;

      // Wait a moment to show the email sending state
      await new Promise(resolve => setTimeout(resolve, 1000));

      setProcessStep('Complete!');
      onConfirm(createdUser.data.userId);

      // Wait a moment to show completion before closing
      await new Promise(resolve => setTimeout(resolve, 1000));
      onClose();
    } catch (err: any) {
      console.error('Error creating user:', err);
      let errorMessage = 'Failed to create user';

      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
      setIsProcessing(false);
    }
  };

  const getTitle = () => {
    if (startInCreateMode) {
      return 'Create User';
    }
    if (isCreatingNew) {
      return 'New User';
    }
    switch (type) {
      case 'operator':
        return 'Add Operator';
      case 'staff':
        return 'Add Staff';
      case 'owner':
        return 'Change Owner';
      default:
        return 'Add User';
    }
  };

  const getConfirmButtonText = () => {
    if (startInCreateMode) {
      return 'Create User';
    }
    switch (type) {
      case 'operator':
        return 'Add Operator';
      case 'staff':
        return 'Add Staff';
      case 'owner':
        return 'Change Owner';
      default:
        return 'Confirm';
    }
  };

  // Get selected user display info
  const getSelectedUserDisplay = () => {
    if (!selectedUserId) return 'Select a user...';
    const selectedUser = users.find(user => user.userId === selectedUserId);
    if (!selectedUser) return 'Select a user...';

    const formattedUserId = selectedUser.displayUserId.replace('PWP-U-', '');
    return `${formattedUserId} (${selectedUser.email}) ${selectedUser.name}`;
  };

  const handleUserSelect = (userId: string) => {
    setSelectedUserId(userId);
    setIsDropdownOpen(false);
    setSearchTerm('');
    setHighlightedIndex(-1);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content">
        <div className="modal-header">
          <h2>{getTitle()}</h2>
          {!startInCreateMode && (
            <button
              className="new-user-button"
              onClick={() => setIsCreatingNew(!isCreatingNew)}
            >
              {isCreatingNew ? 'Select Existing' : '+ New User'}
            </button>
          )}
        </div>

        {isProcessing ? (
          <div className="processing-container">
            <div className="spinner"></div>
            <p>{processStep}</p>
          </div>
        ) : (
          <>
            {isCreatingNew ? (
              <div className="new-user-form">
                <div className="form-group">
                  <label>Name:</label>
                  <input
                    type="text"
                    value={newUser.name}
                    onChange={(e) => setNewUser({ ...newUser, name: e.target.value })}
                    placeholder="Enter name"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Email:</label>
                  <input
                    type="email"
                    value={newUser.email.toLowerCase()}
                    onChange={(e) => {
                      setNewUser({ ...newUser, email: e.target.value.toLowerCase() });
                      setEmailError(null);
                    }}
                    placeholder="Enter email"
                    required
                  />
                  {emailError && <div className="error">{emailError}</div>}
                </div>
                <div className="form-group">
                  <label>Temp Password:</label>
                  <input
                    type="text"
                    value={newUser.password}
                    onChange={(e) => setNewUser({ ...newUser, password: e.target.value })}
                    placeholder="Enter temp password"
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Phone Number:</label>
                  <input
                    type="tel"
                    value={newUser.phoneNo}
                    onChange={(e) => setNewUser({ ...newUser, phoneNo: e.target.value })}
                    placeholder="Enter phone number (optional)"
                  />
                </div>
                <div className="form-group">
                  <label>Role:</label>
                  <select
                    value={newUser.role}
                    onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  >
                    <option value="Operator">Operator</option>
                    <option value="SuperAdmin">Super Admin</option>
                  </select>
                </div>
              </div>
            ) : (
              <div className="select-container">
                <div className={`custom-select-container ${isDropdownOpen ? 'open' : ''}`} ref={dropdownRef}>
                  <div
                    className="select-display"
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                  >
                    <span className="selected-text">{getSelectedUserDisplay()}</span>
                    <span className="select-arrow">▼</span>
                  </div>

                  {isDropdownOpen && (
                    <div className="select-dropdown">
                      <div className="search-input-container">
                        <input
                          type="text"
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          placeholder="Search by name, email, or ID..."
                          className="search-input"
                          onClick={(e) => e.stopPropagation()}
                        />
                      </div>

                      <div className="dropdown-options">
                        {filteredUsers.length === 0 ? (
                          <div className="no-results">No users found</div>
                        ) : (
                          filteredUsers.map((user, index) => {
                            const formattedUserId = user.displayUserId.replace('PWP-U-', '');
                            return (
                              <div
                                key={user.userId}
                                className={`dropdown-option ${selectedUserId === user.userId ? 'selected' : ''} ${highlightedIndex === index ? 'highlighted' : ''}`}
                                onClick={() => handleUserSelect(user.userId)}
                                onMouseEnter={() => setHighlightedIndex(index)}
                              >
                                <div className="user-option-info">
                                  <div className="user-option-id">{formattedUserId}</div>
                                  <div className="user-option-email">{user.email}</div>
                                  <div className="user-option-name">{user.name}</div>
                                </div>
                                <div className="user-option-status">
                                  {user.isVerified && <span className="verified-badge">✓</span>}
                                  {user.isBanned && <span className="banned-badge">🚫</span>}
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            )}

            {error && <div className="error">{error}</div>}

            <div className="button-container">
              <button
                className="confirm-button"
                onClick={isCreatingNew ? handleCreateUser : () => selectedUserId && onConfirm(selectedUserId)}
                disabled={isCreatingNew ?
                  !newUser.name || !newUser.email || !newUser.password || isProcessing :
                  !selectedUserId
                }
              >
                {getConfirmButtonText()}
              </button>
              <button className="cancel-button" onClick={onClose} disabled={isProcessing}>
                Cancel
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default AddUser; 